import { useCallback, useState } from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import { useHubSnackBar } from "../../../hooks";
import { usePostQuoteCostingSheet } from "../../../hooks/react-query/useSalesData";

const PAGE_IDS = ["page-1", "costing-attachments"];

const onclone = (doc) => {
  const replaceWithSpan = (el) => {
    const span = doc.createElement("span");
    const cs = el.ownerDocument.defaultView.getComputedStyle(el);
    span.textContent = el.value || el.placeholder || "";
    span.style.cssText = cs.cssText;
    span.style.display = "inline-flex";
    span.style.alignItems = "center";
    span.style.whiteSpace =
      el.tagName === "TEXTAREA" ? "pre-wrap" : "nowrap";
    span.style.overflow = "hidden";
    span.style.boxSizing = "border-box";
    el.parentNode.replaceChild(span, el);
  };
  doc
    .querySelectorAll(
      'input[type="text"], input:not([type]), input[type="number"], textarea',
    )
    .forEach((el) => {
      if (
        el.classList.contains("MuiSelect-nativeInput") ||
        el.getAttribute("aria-hidden") === "true" ||
        el.tabIndex === -1
      ) {
        return;
      }
      replaceWithSpan(el);
    });
};

export const useCostingSheetUpload = ({ quoteId } = {}) => {
  const [working, setWorking] = useState(false);
  const { mutate, isLoading } = usePostQuoteCostingSheet();
  const { addSnackbarError } = useHubSnackBar();

  const upload = useCallback(async () => {
    if (!quoteId || working || isLoading) return;

    const nodes = PAGE_IDS.map((id) => document.getElementById(id)).filter(
      Boolean,
    );
    if (!nodes.length) {
      addSnackbarError({
        message: "Switch to the Costing Sheet view first.",
      });
      return;
    }

    setWorking(true);
    try {
      const canvases = [];
      for (const n of nodes) {
        // eslint-disable-next-line no-await-in-loop
        const c = await html2canvas(n, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          onclone,
        });
        canvases.push(c);
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "in",
        format: "letter",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      canvases.forEach((canvas, idx) => {
        if (idx > 0) pdf.addPage();
        const imgData = canvas.toDataURL("image/jpeg", 0.85);
        const ratio = canvas.height / canvas.width;
        const imgWidth = pageWidth;
        const imgHeight = pageWidth * ratio;
        if (imgHeight <= pageHeight) {
          pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
        } else {
          const scaledHeight = pageHeight;
          const scaledWidth = scaledHeight / ratio;
          const xOffset = (pageWidth - scaledWidth) / 2;
          pdf.addImage(imgData, "JPEG", xOffset, 0, scaledWidth, scaledHeight);
        }
      });

      const blob = pdf.output("blob");
      const file = new File([blob], "costing-sheet.pdf", {
        type: "application/pdf",
      });

      mutate({ id: quoteId, file });
    } catch (e) {
      console.log("[useCostingSheetUpload] capture failed:", e);
      addSnackbarError({
        message: "Failed to render the costing sheet PDF.",
      });
    } finally {
      setWorking(false);
    }
  }, [quoteId, working, isLoading, addSnackbarError, mutate]);

  return { upload, working: working || isLoading };
};

export default useCostingSheetUpload;
